import { subscribeToIntelligenceLane } from '@/services/intelligenceWidgets.js';
import { getMarketSnapshots, getProfile } from '@/services/profile.js';
import type {
    IntelligenceFeedItem,
    ProfileMarketSnapshot,
    SecondOrderDomainKey,
    Storyline,
} from '@akem/shared';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
    getAllStorylines,
    getIntelligenceFeed,
    getStorylineById,
    type IntelligenceFeedResult
} from '../../../services/feed.js';
import {
    getProfileStorylines,
    refreshIntelligence,
    type StorylineSummary,
} from '../../../services/intelligence.js';
import { resolveInstrumentId } from '../../../services/secondOrder.js';
import { MarketFooterStrip } from '../../intelligence/components/MarketFooterStrip.tsx';
import { SimplifiedIntelligenceCard } from '../../intelligence/components/SimplifiedIntelligenceCard.js';
import { useStorylineChatContext } from '../../intelligence/components/StorylineChatContext.tsx';
import { StorylineDetails } from '../../intelligence/components/StorylineDetails.tsx';
import { useProfileNavigation } from '../hooks/useProfileNavigation.ts';
import type { WidgetDefinition, WidgetProps } from '../types/index.js';

import {
    type StorylineArtifactChip,
    type StorylineDirection,
} from '../../intelligence/components/StorylineArtifactCard.tsx';
// Consuming normalized Intelligence Feed

const DOMAIN_LABEL: Record<SecondOrderDomainKey, string> = {
  marketTrends: 'Market Trends',
  politics: 'Politics',
  regulatory: 'Regulatory',
  mna: 'M&A',
  logistics: 'Logistics',
  customers: 'Customers',
  suppliers: 'Suppliers',
  themes: 'Themes',
};

const FEED_STALE_TIME_MS = 25_000;
const FEED_CACHE_TIME_MS = 5 * 60 * 1000;

type IntelligenceFeedQueryData = Pick<IntelligenceFeedResult, 'items' | 'etag'>;

const buildFeedQueryKey = (
  instrumentId: string,
  domain: SecondOrderDomainKey,
  limit: number
) => ['intelligenceFeed', instrumentId, domain, limit] as const;

const deriveItemDirection = (item: IntelligenceFeedItem): StorylineDirection => {
  const explicit = (item as any).direction as StorylineDirection | undefined;
  if (explicit === 'positive' || explicit === 'negative' || explicit === 'neutral') {
    return explicit;
  }
  const imp: any = item.impact;
  if (imp && typeof imp === 'object') {
    if (typeof imp.direction === 'string') {
      if (imp.direction === 'positive') return 'positive';
      if (imp.direction === 'negative') return 'negative';
      return 'neutral';
    }
    if (typeof imp.magnitude === 'number') {
      if (imp.magnitude > 0) return 'positive';
      if (imp.magnitude < 0) return 'negative';
      return 'neutral';
    }
  }
  return 'neutral';
};

const formatStorylineCreationLabel = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

interface AkemIntelligenceWidgetConfig {
  domain?: SecondOrderDomainKey;
  profileId?: string;
  profileIds?: string[];
  title?: string;
  maxArtifacts?: number;
  viewMode?: 'all' | 'portfolio';
}

type AggregatedItem = IntelligenceFeedItem & {
  sourceProfileId?: string;
  contributingProfiles?: string[];
};

const storylineSummaryToFeedItem = (summary: StorylineSummary): IntelligenceFeedItem => {
  const chips: IntelligenceFeedItem['chips'] = ['Storyline', 'Qualitative'];
  const item: IntelligenceFeedItem = {
    id: `storyline:${summary.id}`,
    section: summary.domain,
    instrumentIds: [summary.instrumentId],
    title: summary.title,
    summary: summary.title,
    ts: summary.asOf,
    chips,
    importance: 0.6,
    storylineId: summary.id,
    sources: summary.monitorId
      ? [{ label: `Monitor ${summary.monitorId}` }]
      : [{ label: 'Storyline' }],
    tags: summary.monitorId ? ['monitor'] : undefined,
    schemaVersion: 'feed.v0.1',
  };
  if (summary.direction) {
    (item as any).direction = summary.direction;
  }
  return item;
};

export const AkemIntelligenceWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateState,
  onUpdateOutputs,
}) => {
  const config = (widget.config || {}) as AkemIntelligenceWidgetConfig;
  const domain = config.domain ?? 'marketTrends';
  const title = config.title ?? DOMAIN_LABEL[domain];

  const normalizedProfileIds = React.useMemo(() => {
    const raw = Array.isArray(config.profileIds) ? config.profileIds : [];
    const deduped = new Set<string>();
    const out: string[] = [];
    for (const value of raw) {
      if (typeof value !== 'string') continue;
      const trimmed = value.trim();
      if (!trimmed || deduped.has(trimmed)) continue;
      deduped.add(trimmed);
      out.push(trimmed);
    }
    return out;
  }, [config.profileIds]);

  const singleProfileId = React.useMemo(() => {
    if (typeof config.profileId !== 'string') return undefined;
    const trimmed = config.profileId.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }, [config.profileId]);

  const activeProfileIds = React.useMemo(() => {
    if (normalizedProfileIds.length > 0) return normalizedProfileIds;
    if (singleProfileId) return [singleProfileId];
    return [];
  }, [normalizedProfileIds, singleProfileId]);

  const maxArtifacts =
    typeof config.maxArtifacts === 'number' && config.maxArtifacts > 0
      ? Math.min(Math.floor(config.maxArtifacts), 12)
      : 4;

  const [items, setItems] = React.useState<AggregatedItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [warning, setWarning] = React.useState<string | null>(null);
  const [modalItem, setModalItem] = React.useState<AggregatedItem | null>(null);
  const [modalStoryline, setModalStoryline] = React.useState<Storyline | null>(null);
  const [modalLoading, setModalLoading] = React.useState<boolean>(false);
  const [snapshots, setSnapshots] = React.useState<Record<string, ProfileMarketSnapshot | null>>({});
  // Feed-driven state
  const isMountedRef = React.useRef(true);
  const requestIdRef = React.useRef(0);
  const instrumentCacheRef = React.useRef(new Map<string, string>());
  const queryClient = useQueryClient();

  const { openProfile } = useProfileNavigation();
  const { setActiveStoryline } = useStorylineChatContext();

  // Removed modal contributors chip (redundant with Sources section)

  const modalProfileId = React.useMemo(() => {
    if (!modalItem) return undefined;
    if (typeof modalItem.sourceProfileId === 'string' && modalItem.sourceProfileId.trim().length > 0) {
      return modalItem.sourceProfileId.trim();
    }
    return activeProfileIds[0];
  }, [modalItem, activeProfileIds]);

  // Prefer primarySymbol; fallback to instrumentId suffix for robustness
  const modalTicker = React.useMemo(() => {
    const instr = Array.isArray(modalItem?.instrumentIds) && modalItem!.instrumentIds.length
      ? modalItem!.instrumentIds[0]
      : '';
    if (instr && instr.includes(':')) {
      const suffix = instr.split(':').pop()?.trim();
      return suffix && suffix.length > 0 ? suffix : undefined;
    }
    return undefined;
  }, [modalItem?.instrumentIds]);

  const modalCreationLabel = React.useMemo(() => {
    const iso = modalStoryline?.asOf || modalStoryline?.trigger_event?.ts || modalItem?.ts || null;
    return formatStorylineCreationLabel(iso);
  }, [modalStoryline?.asOf, modalStoryline?.trigger_event?.ts, modalItem?.ts]);

  const handleNavigateToProfile = React.useCallback(
    (targetProfileId?: string | null, ticker?: string | null) => {
      if (typeof targetProfileId === 'string' && targetProfileId.trim().length > 0) {
        openProfile({ profileId: targetProfileId.trim() });
        return;
      }
      if (typeof ticker === 'string' && ticker.trim().length > 0) {
        openProfile({ ticker: ticker.trim() });
      }
    },
    [openProfile]
  );

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resolveInstrumentForProfile = React.useCallback(
    async (profileId: string): Promise<string | null> => {
      const trimmed = typeof profileId === 'string' ? profileId.trim() : '';
      if (!trimmed) return null;
      const cached = instrumentCacheRef.current.get(trimmed);
      if (cached) return cached;

      const lower = trimmed.toLowerCase();
      if (lower.startsWith('instrument:')) {
        instrumentCacheRef.current.set(trimmed, trimmed);
        return trimmed;
      }

      // Do not attempt to coerce arbitrary ids into instrumentIds. Only accept
      // values that already start with `instrument:`; for everything else, resolve
      // via profile lookup or ticker.

      try {
        const profile = await getProfile({ idOrTicker: trimmed, useMock: false });
        if (profile?.instrumentId) {
          const id = profile.instrumentId.trim();
          if (id) {
            instrumentCacheRef.current.set(trimmed, id);
            return id;
          }
        }
        if (profile?.ticker) {
          const viaTicker = resolveInstrumentId({ ticker: profile.ticker });
          if (viaTicker && viaTicker.toLowerCase().startsWith('instrument:')) {
            instrumentCacheRef.current.set(trimmed, viaTicker);
            return viaTicker;
          }
        }
      } catch (err) {
        console.warn('[AkemIntelligenceWidget] failed to resolve instrument via profile', {
          profileId: trimmed,
          err,
        });
      }

      // Last fallback: if the profile id already resembles a ticker, try direct build
      const fallback = resolveInstrumentId({ ticker: trimmed });
      if (fallback && fallback.toLowerCase().startsWith('instrument:')) {
        instrumentCacheRef.current.set(trimmed, fallback);
        return fallback;
      }
      return null;
    },
    []
  );

  const fetchFeed = React.useCallback(
    async (opts?: { silent?: boolean; resetOutputs?: boolean; forceRefetch?: boolean }) => {
      const shouldResetOutputs = opts?.resetOutputs ?? !opts?.silent;
      const shouldForceRefetch = Boolean(opts?.silent || opts?.forceRefetch);
      if (shouldResetOutputs) {
        onUpdateOutputs?.({ selectedArtifact: null, selectedReport: null });
      }

      const profileIds = activeProfileIds;
      const isAllMode = config.viewMode === 'all' || (!profileIds.length && config.viewMode !== 'portfolio');

      // Handle "All" mode - fetch storylines across all instruments
      if (isAllMode) {
        const requestId = ++requestIdRef.current;

        if (!opts?.silent) {
          setLoading(true);
          setError(null);
          setWarning(null);
        }

        try {
          const result = await getAllStorylines({
            domain: domain,
            limit: maxArtifacts,
          });

          if (!isMountedRef.current || requestId !== requestIdRef.current) return;

          // API returns IntelligenceFeedItems already, so just convert to AggregatedItems
          const aggregatedItems: AggregatedItem[] = result.items.map((item: IntelligenceFeedItem) => ({
            ...item,
            sourceProfileId: undefined,
            contributingProfiles: [],
          }));

          setItems(aggregatedItems);
          setError(null);
          setWarning(null);
          onUpdateState?.({ loading: false, data: aggregatedItems });
        } catch (err) {
          if (!isMountedRef.current || requestId !== requestIdRef.current) return;
          console.error('[AkemIntelligenceWidget] failed to fetch all storylines', err);
          const errMsg = err instanceof Error ? err.message : 'Failed to load storylines';
          setError(errMsg);
          setItems([]);
          onUpdateState?.({ error: errMsg, loading: false });
        } finally {
          if (isMountedRef.current && requestId === requestIdRef.current && !opts?.silent) {
            setLoading(false);
          }
        }
        return;
      }

      // Portfolio mode - existing logic
      if (!profileIds.length) {
        setItems([]);
        setWarning(null);
        const message = 'Profile context required';
        if (!opts?.silent) {
          setError(message);
          onUpdateState?.({ error: message, loading: false });
        }
        if (!shouldResetOutputs) {
          onUpdateOutputs?.({ selectedArtifact: null, selectedReport: null });
        }
        return;
      }

      const requestId = ++requestIdRef.current;

      if (!opts?.silent) {
        setLoading(true);
        setError(null);
        setWarning(null);
      }

      try {
        const aggregated = new Map<string, AggregatedItem>();
        const failed: string[] = [];

        const appendItem = (pid: string, item: IntelligenceFeedItem) => {
          const key = item.id;
          const normalized: AggregatedItem = {
            ...item,
            sourceProfileId: pid,
            contributingProfiles: [pid],
          };
          const existing = aggregated.get(key);
          if (!existing) {
            aggregated.set(key, normalized);
          } else {
            const merged = new Set<string>(existing.contributingProfiles ?? []);
            merged.add(pid);
            existing.contributingProfiles = Array.from(merged);
            aggregated.set(key, existing);
          }
        };

        const fetchStorylineSummaries = async () => {
          await Promise.all(
            profileIds.map(async (pid) => {
              try {
                const items = await getProfileStorylines(pid, domain, {
                  limit: maxArtifacts,
                });
                if (!items.length) {
                  failed.push(pid);
                  return;
                }
                items
                  .map(storylineSummaryToFeedItem)
                  .forEach((item) => appendItem(pid, item));
              } catch (err) {
                failed.push(pid);
                if (!opts?.silent) {
                  console.warn('[AkemIntelligenceWidget] failed to load storyline feed', {
                    profileId: pid,
                    domain,
                    err,
                  });
                }
              }
            })
          );
        };

        const fetchLegacyFeed = async () => {
          await Promise.all(
            profileIds.map(async (pid) => {
              try {
                const instrumentId = await resolveInstrumentForProfile(pid);
                if (!instrumentId) throw new Error('instrumentId not resolved');

                const queryKey = buildFeedQueryKey(instrumentId, domain, maxArtifacts);
                let previous =
                  queryClient.getQueryData<IntelligenceFeedQueryData>(queryKey) ?? null;

                if (shouldForceRefetch) {
                  await queryClient.invalidateQueries({ queryKey, refetchType: 'inactive' });
                }

                const queryData = await queryClient.fetchQuery<IntelligenceFeedQueryData>({
                  queryKey,
                  staleTime: FEED_STALE_TIME_MS,
                  gcTime: FEED_CACHE_TIME_MS,
                  queryFn: async ({ signal }) => {
                    const response = await getIntelligenceFeed(instrumentId, {
                      section: domain,
                      limit: maxArtifacts,
                      onlyStorylines: true,
                      signal,
                      ifNoneMatch: previous?.etag ?? null,
                    });
                    if (response.notModified && previous) {
                      return previous;
                    }
                    return {
                      items: response.items,
                      etag: response.etag ?? null,
                    };
                  },
                });

                const sourceItems = queryData.items ?? [];
                const filtered = sourceItems.filter((it) => it.section === domain);
                const relevant = filtered.length > 0 ? filtered : sourceItems;

                relevant.forEach((it) => appendItem(pid, it));
              } catch (err) {
                failed.push(pid);
                if (!opts?.silent) {
                  console.warn('[AkemIntelligenceWidget] failed to load feed', {
                    profileId: pid,
                    domain,
                    err,
                  });
                }
              }
            })
          );
        };

        await fetchStorylineSummaries();
        if (aggregated.size === 0 && failed.length === profileIds.length) {
          aggregated.clear();
          failed.splice(0, failed.length);
          await fetchLegacyFeed();
        }

        const merged = Array.from(aggregated.values()).sort((a, b) => {
          const at = Date.parse(a.ts as any) || 0;
          const bt = Date.parse(b.ts as any) || 0;
          return bt - at;
        });

        const limited = merged.slice(0, maxArtifacts);
        const allFailed = failed.length === profileIds.length && limited.length === 0;
        const partialFailed = failed.length > 0 && failed.length < profileIds.length;

        const errorMessage = allFailed ? 'Failed to load intelligence' : null;
        const warningMessage = partialFailed ? `Missing updates for ${failed.join(', ')}` : null;

        setItems(limited);
        setError(errorMessage);
        setWarning(warningMessage);

        onUpdateState?.({ data: limited, loading: false, ...(errorMessage ? { error: errorMessage } : {}) });
      } catch (err: any) {
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        const message = err?.message || 'Failed to load intelligence';
        if (opts?.silent) {
          console.warn('[AkemIntelligenceWidget] silent refresh failed', err);
        } else {
          setError(message);
          setWarning(null);
          onUpdateState?.({ error: message, loading: false });
        }
      } finally {
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    },
    [
      activeProfileIds,
      config.viewMode,
      domain,
      maxArtifacts,
      onUpdateOutputs,
      onUpdateState,
      queryClient,
      resolveInstrumentForProfile,
    ]
  );

  React.useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Load market snapshots for displayed storyline items to power sparklines
  React.useEffect(() => {
    const tickers = Array.from(
      new Set(
        items
          .map((it) => {
            const raw = Array.isArray(it.instrumentIds) && it.instrumentIds.length ? it.instrumentIds[0] : '';
            if (!raw) return null;
            const parts = String(raw).split(':');
            const symbol = parts[parts.length - 1]?.trim();
            return symbol ? symbol.toUpperCase() : null;
          })
          .filter(Boolean) as string[]
      )
    );
    if (tickers.length === 0) {
      setSnapshots({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snaps = await getMarketSnapshots(tickers, 60).catch(() => ({} as Record<string, ProfileMarketSnapshot | null>));
        if (!cancelled) setSnapshots(snaps);
      } catch {
        if (!cancelled) setSnapshots({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  React.useEffect(() => {
    if (!activeProfileIds.length) return;
    const unsubscribes = activeProfileIds.map((pid) =>
      subscribeToIntelligenceLane(pid, domain, (update) => {
        if (update?.instrumentId) {
          const queryKey = buildFeedQueryKey(update.instrumentId, domain, maxArtifacts);
          queryClient.invalidateQueries({ queryKey, refetchType: 'inactive' });
        }
        fetchFeed({ silent: true, forceRefetch: true });
      })
    );
    return () => {
      unsubscribes.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch {}
      });
    };
  }, [activeProfileIds, domain, fetchFeed, maxArtifacts, queryClient]);

  const handleRefresh = React.useCallback(async () => {
    const isAllMode = config.viewMode === 'all' || (!activeProfileIds.length && config.viewMode !== 'portfolio');
    
    if (isAllMode) {
      // In "all" mode, just refetch the data
      fetchFeed({ forceRefetch: true });
      return;
    }
    
    if (!activeProfileIds.length) return;
    await Promise.all(
      activeProfileIds.map((pid) =>
        refreshIntelligence(pid, [domain]).catch((err) => {
          console.warn('[AkemIntelligenceWidget] refresh failed', { profileId: pid, domain, err });
        })
      )
    );
    fetchFeed({ forceRefetch: true });
  }, [activeProfileIds, config.viewMode, domain, fetchFeed]);

  const viewAllHref = React.useMemo(() => {
    if (activeProfileIds.length !== 1) return null;
    const pid = activeProfileIds[0];
    return `/profiles/${encodeURIComponent(pid)}/intelligence/${domain}`;
  }, [activeProfileIds, domain]);

  const openModal = React.useCallback(
    async (item: AggregatedItem) => {
      setModalItem({ ...item });
      setModalStoryline(null);
      setModalLoading(true);
      // Map to legacy output shape for back-compat consumers
      const dir = deriveItemDirection(item);
      onUpdateOutputs?.({
        selectedArtifact: {
          id: item.id,
          title: item.title,
          domain: item.section,
          direction: dir,
          impactScore: item.importance ?? 0.5,
          severity: 'normal',
          ts: Date.parse(item.ts as any) || Date.now(),
          primarySymbol: undefined,
        },
        selectedReport: null,
      });
      try {
        if (item.storylineId) {
          const story = await getStorylineById(item.storylineId).catch(() => null);
          if (story) {
            setModalStoryline(story);
            setActiveStoryline(story);
          }
        }
      } catch (err) {
        console.error('[AkemIntelligenceWidget] storyline fetch failed', err);
      } finally {
        setModalLoading(false);
      }
    },
    [onUpdateOutputs]
  );

  const closeModal = React.useCallback(() => {
    setModalItem(null);
    setModalStoryline(null);
    setModalLoading(false);
    onUpdateOutputs?.({ selectedArtifact: null, selectedReport: null });
    setActiveStoryline(null);
  }, [onUpdateOutputs]);

  const header = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.25 }}>
      <Typography variant="subtitle1">{title}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {viewAllHref ? (
          <Button
            size="small"
            variant="text"
            component="a"
            href={viewAllHref}
            sx={{ textTransform: 'none' }}
          >
            View all
          </Button>
        ) : null}
        <Tooltip title="Refresh intelligence">
          <span>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Storyline details dialog removed */}
    </Stack>
  );

  let body: React.ReactNode = null;
  const isAllMode = config.viewMode === 'all' || (!activeProfileIds.length && config.viewMode !== 'portfolio');
  if (!isAllMode && !activeProfileIds.length) {
    body = (
      <Typography variant="body2" color="text.secondary">
        Attach this widget to one or more profiles to view intelligence.
      </Typography>
    );
  } else if (loading) {
    body = (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }} spacing={1}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="text.secondary">
          Analyzing signals…
        </Typography>
      </Stack>
    );
  } else if (error) {
    body = (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  } else if (!items.length) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No intelligence yet. Refresh to generate the latest assessment.
      </Typography>
    );
  } else {
    body = (
      <Stack spacing={1.5}>
        {items.map((item) => {
          const contributors = Array.isArray(item.contributingProfiles)
            ? item.contributingProfiles.filter(Boolean)
            : [];
          const contributorChipLabel =
            contributors.length > 1
              ? `${contributors[0]} +${contributors.length - 1}`
              : contributors[0] ?? null;
          const contributorTooltip =
            contributors.length > 1 ? contributors.join(', ') : undefined;
          const direction = deriveItemDirection(item);
          const rawInstr =
            Array.isArray(item.instrumentIds) && item.instrumentIds.length
              ? String(item.instrumentIds[0])
              : '';
          const parts = rawInstr.split(':');
          const ticker = (parts[parts.length - 1] || '').toUpperCase();
          const snap = ticker ? snapshots[ticker] : undefined;
          const sparkline = Array.isArray(snap?.sparkline) ? snap!.sparkline : undefined;
          const color =
            snap?.changePercent != null
              ? snap.changePercent > 0
                ? '#34C759'
                : snap.changePercent < 0
                ? '#FF3B30'
                : '#8B66FF'
              : '#8B66FF';
          const chipItems: StorylineArtifactChip[] = [];
          if (contributorChipLabel) {
            chipItems.push({
              label: contributorChipLabel,
              tooltip: contributorTooltip,
              color: '#7A8FFF',
            });
          }
          if (Array.isArray(item.chips)) {
            item.chips.slice(0, 2).forEach((chip) => {
              if (typeof chip === 'string' && chip.trim()) {
                chipItems.push({ label: chip.trim() });
              }
            });
          }
          const summary =
            typeof item.summary === 'string' && item.summary.trim().length > 0
              ? item.summary
              : undefined;
          return (
            <SimplifiedIntelligenceCard
              key={item.id}
              title={item.title}
              ticker={ticker || undefined}
              sparkline={sparkline}
              sparklineColor={color}
              direction={direction}
              onClick={() => openModal(item)}
              onTickerClick={(symbol) => handleNavigateToProfile(null, symbol)}
            />
          );
        })}
        {warning ? (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, px: 2 }}>
            {warning}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {header}
      <Divider />
      <Box sx={{ flex: 1, p: 0, overflow: 'auto' }}>{body}</Box>

      <Dialog
        open={Boolean(modalItem)}
        onClose={closeModal}
        fullWidth
        maxWidth="lg"
        disableEnforceFocus
        disableRestoreFocus
        disableAutoFocus
        PaperProps={{
        sx: {
          width: { xs: '100%', sm: 'min(1100px, 92vw)' },
          maxWidth: '92vw',
          height: { xs: '100%', sm: '90vh' },
          backgroundImage: 'none',
          backgroundColor: (theme) => theme.palette.background.paper,
          backdropFilter: 'none',
        },
      }}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Stack spacing={0.5} flex={1} minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6">{modalStoryline?.title || modalItem?.title || ''}</Typography>
                {modalStoryline?.decision?.picked ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={(modalStoryline.decision.picked || '').toUpperCase()}
                    sx={{
                      fontWeight: 600,
                      color: modalStoryline.decision.picked === 'positive'
                        ? 'success.main'
                        : modalStoryline.decision.picked === 'negative'
                        ? 'error.main'
                        : 'text.secondary',
                      borderColor: modalStoryline.decision.picked === 'positive'
                        ? 'success.main'
                        : modalStoryline.decision.picked === 'negative'
                        ? 'error.main'
                        : 'text.secondary',
                    }}
                  />
                ) : null}
              </Stack>
              {modalCreationLabel ? (
                <Typography variant="caption" color="text.secondary">
                  Created {modalCreationLabel}
                </Typography>
              ) : null}
            </Stack>
            <IconButton size="small" onClick={closeModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 'calc(90vh - 140px)' }}>
          {modalItem ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {modalTicker ? (
                  <Chip
                    size="small"
                    label={modalTicker}
                    variant="outlined"
                    onClick={() => handleNavigateToProfile(modalProfileId, modalTicker)}
                    sx={{ cursor: 'pointer' }}
                  />
                ) : null}
                {Array.isArray(modalItem.chips)
                  ? modalItem.chips.slice(0, 4).map((chip) => (
                      <Chip key={chip} size="small" label={chip} variant="outlined" />
                    ))
                  : null}
              </Stack>
              {modalLoading ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading storyline…
                  </Typography>
                </Stack>
              ) : modalStoryline ? (
                // Render report-style details for storyline
                <StorylineDetails storyline={modalStoryline} />
              ) : (
                <Stack>
                  <Typography variant="body2">{modalItem.summary}</Typography>
                  {modalItem.impact && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Impact: {typeof (modalItem.impact as any).affected_metric === 'string' ? (modalItem.impact as any).affected_metric : '—'}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        {modalStoryline ? (
          <DialogActions sx={{ px: 0, width: '100%', '& > *': { flex: 1 } }}>
            <MarketFooterStrip
              symbol={modalTicker}
              profileId={modalProfileId}
              articleTimestamp={modalStoryline.asOf || null}
              onNavigateToProfile={handleNavigateToProfile}
            />
          </DialogActions>
        ) : null}
      </Dialog>
    </Box>
  );
};

export const AkemIntelligenceWidgetDefinition: WidgetDefinition = {
  type: 'akemIntelligence',
  name: 'Akem Intelligence',
  description: 'Read-only intelligence feed for a selected domain. Click entries to view storyline or impact details.',
  category: 'analysis',
  icon: InsightsIcon,
  component: AkemIntelligenceWidget,
  defaultConfig: {
    title: 'Akem Intelligence',
    domain: 'marketTrends',
  },
  defaultSize: {
    width: 400,
    height: 320,
  },
  inputDefinitions: [],
  outputDefinitions: [
    {
      id: 'selectedArtifact',
      name: 'Selected Artifact',
      type: 'object',
      description: 'Currently highlighted intelligence artifact with metadata and scoring.',
      schema: {
        type: 'object',
        description: 'IntelligenceArtifact (see packages/shared/src/contracts/schemas.ts)',
        properties: {
          id: { type: 'string' },
          title: { type: 'string' },
          domain: { type: 'string', constraints: { enum: ['marketTrends', 'politics', 'regulatory', 'mna', 'logistics', 'customers', 'suppliers', 'themes'] } },
          direction: { type: 'string', constraints: { enum: ['positive', 'neutral', 'negative'] } },
          impactScore: { type: 'number', constraints: { min: 0, max: 1 } },
          severity: { type: 'string', constraints: { enum: ['low', 'normal', 'elevated', 'high'] } },
          ts: { type: 'number', description: 'Unix timestamp (ms)' },
          primarySymbol: { type: 'string' },
        },
      },
    },
    {
      id: 'selectedReport',
      name: 'Selected Report',
      type: 'object',
      description: 'Structured intelligence report associated with the selected artifact.',
      schema: {
        type: 'object',
        description: 'IntelligenceReport (see packages/shared/src/contracts/schemas.ts)',
        properties: {
          artifactId: { type: 'string' },
          summary: { type: 'string' },
          likelyOutcome: { type: 'string' },
          decision: {
            type: 'object',
            properties: {
              picked: { type: 'string', constraints: { enum: ['positive', 'neutral', 'negative'] } },
              rationale: { type: 'string' },
            },
          },
        },
      },
    },
  ],
};
